import {
  GetReviews,
  CreateReview,
  UpdateReview,
  UpdateReviewScreeningVotesRequired,
  UpdateReviewFullTextReviewVotesRequired,
  UpdateReviewDataExtractionReportsRequired,
  UpdateReviewDataExtractionDraftFields,
  RevertDataExtractionTemplateDraft,
  PublishDataExtractionTemplateDraft,
  ArchiveReview,
  UnarchiveReview,
  DeleteReview,
  MarkRemainingScreenedNotRelevant,
  ExportReview,
} from '@/graphql';
import { Review } from '@/models';
import { apolloClient } from '@/plugins/apollo';
import gql from 'graphql-tag';

const actions = {
  async fetchRequiredScreeningVotes(context, reviewId) {
    const response = await apolloClient.query({
      query: gql`
        query GetReviewScreeningVotesRequired($id: ID!) {
          review(id: $id) {
            id
            screeningVotesRequired
          }
        }
      `,
      variables: {
        id: reviewId,
      },
    });
    return response.data.review.screeningVotesRequired;
  },
  async fetchRequiredFullTextReviewVotes(context, reviewId) {
    const response = await apolloClient.query({
      query: gql`
        query GetReviewFullTextReviewVotesRequired($id: ID!) {
          review(id: $id) {
            id
            fullTextReviewVotesRequired
          }
        }
      `,
      variables: {
        id: reviewId,
      },
    });
    return response.data.review.fullTextReviewVotesRequired;
  },
  async saveReview(context, { review }) {
    const mutation = review.id ? UpdateReview : CreateReview;
    const response = await apolloClient.mutate({
      mutation,
      variables: {
        review: {
          ...review.creationData(),
        },
      },
      update: apolloCacheUpdates.updateReviewsCache,
    });

    const newReview = review.id
      ? response.data.updateReview
      : response.data.createReview;

    return newReview;
  },
  async archiveReview(context, review) {
    const response = await apolloClient.mutate({
      mutation: ArchiveReview,
      variables: {
        id: review.id,
      },
      update: (store, ctx) =>
        apolloCacheUpdates.updateReviewsCache(store, ctx, { review }),
    });

    return response.data.updateReview;
  },
  async unarchiveReview(context, review) {
    const response = await apolloClient.mutate({
      mutation: UnarchiveReview,
      variables: {
        id: review.id,
      },
      update: (store, ctx) =>
        apolloCacheUpdates.updateReviewsCache(store, ctx, { review }),
    });

    return response.data.updateReview;
  },
  async deleteReview(context, review) {
    const response = await apolloClient.mutate({
      mutation: DeleteReview,
      variables: { id: review.id },
      update: (store, ctx) =>
        apolloCacheUpdates.updateReviewsCache(store, ctx, { review }),
    });

    return response.data.deleteReview;
  },
  async updateScreeningVotesRequired(
    context,
    { review, screeningVotesRequired, strategy }
  ) {
    const response = await apolloClient.mutate({
      mutation: UpdateReviewScreeningVotesRequired,
      variables: {
        review: review.id,
        screeningVotesRequired,
        strategy,
      },
      optimisticResponse: {
        updateReviewScreeningVotesRequired: {
          ...review,
          screeningVotesRequired,
        },
      },
    });
    return response.data.updateReviewScreeningVotesRequired;
  },
  async updateFullTextReviewVotesRequired(
    context,
    { review, fullTextReviewVotesRequired, strategy }
  ) {
    const response = await apolloClient.mutate({
      mutation: UpdateReviewFullTextReviewVotesRequired,
      variables: {
        review: review.id,
        fullTextReviewVotesRequired,
        strategy,
      },
      optimisticResponse: {
        updateReviewFullTextReviewVotesRequired: {
          ...review,
          fullTextReviewVotesRequired,
        },
      },
    });
    return response.data.updateReviewFullTextReviewVotesRequired;
  },
  async updateDataExtractionReportsRequired(
    context,
    { review, dataExtractionReportsRequired }
  ) {
    const response = await apolloClient.mutate({
      mutation: UpdateReviewDataExtractionReportsRequired,
      variables: {
        review: review.id,
        dataExtractionReportsRequired,
      },
      optimisticResponse: {
        updateReviewDataExtractionReportsRequired: {
          ...review,
          dataExtractionReportsRequired,
        },
      },
    });
    return response.data.updateReviewDataExtractionReportsRequired;
  },
  async updateReviewDataExtractionDraftFields(
    context,
    { reviewId, draftFields }
  ) {
    const response = await apolloClient.mutate({
      mutation: UpdateReviewDataExtractionDraftFields,
      variables: {
        reviewId,
        draftFields: draftFields.map((x) => x.toInput()),
      },
      refetchQueries: ['GetReviewForExtractionTemplateBuilder'],
      awaitRefetchQueries: true,
    });
    return response.data.updateReviewDataExtractionDraftFields;
  },
  async revertDataExtractionTemplateDraft(context, reviewId) {
    const response = await apolloClient.mutate({
      mutation: RevertDataExtractionTemplateDraft,
      variables: { reviewId },
      refetchQueries: ['GetReviewForExtractionTemplateBuilder'],
      awaitRefetchQueries: true,
    });
    return response.data.revertDataExtractionTemplateDraft;
  },
  async publishDataExtractionTemplateDraft(context, reviewId) {
    const response = await apolloClient.mutate({
      mutation: PublishDataExtractionTemplateDraft,
      variables: { reviewId },
      refetchQueries: ['GetReviewForExtractionTemplateBuilder'],
      awaitRefetchQueries: true,
    });
    return response.data.publishDataExtractionTemplateDraft;
  },
  async markRemainingScreenedNotRelevant(
    context,
    { review, reason }
  ) {
    const response = await apolloClient.mutate({
      mutation: MarkRemainingScreenedNotRelevant,
      variables: { reviewId: review.id, reason },
      refetchQueries: [
        'GetReviewForReviewPage',
        'GetReviewForScreeningPage',
        'GetReviewScreeningInsights',
        'GetReferencesByReviewIdAndFilters',
        'SearchReferences',
        'GetRelevanceAssessmentCountsByReviewIdAndFilters',
      ],
    });
    return response.data.markRemainingScreenedNotRelevant;
  },
  async exportReview(context, { review, field, options, revision }) {
    const response = await apolloClient.query({
      query: ExportReview,
      variables: {
        reviewId: review.id,
        groupPdfsByFieldId: field?.id,
        options,
        revisionId: revision?.id,
      },
      fetchPolicy: 'network-only',
    });
    const downloadInfo = response.data.exportReview;
    return downloadInfo.signedDownloadUrl;
  },
};

// These functions should update the Apollo cache once a mutation
// that effects a query's result is executed. Ensures the UI will
// update when using the 'cache-first' fetch policy. These are put
// in the `update` property of the apolloClient.mutate call.
const apolloCacheUpdates = {
  updateReviewsCache: (store, { data }, metadata = null) => {
    let getReviewsCache = null;
    // Read current cache of the "GetReviews" query.
    getReviewsCache = store.readQuery({
      query: GetReviews,
    });

    if (getReviewsCache === null) {
      // If this fails then the query has yet to be cached.
      return;
    }

    // Create a copy of the current cache (required by ApolloClient v3).
    let reviews = getReviewsCache.reviews.map(
      (data) => new Review(data)
    );
    let archivedReviews = getReviewsCache.archivedReviews.map(
      (data) => new Review(data)
    );

    // Update the current cache with the new data.
    if (data.updateReview) {
      // Update existing review in Apollo cache.
      const index = reviews.findIndex(
        (r) => r.id === data.updateReview.id
      );
      const archiveIndex = archivedReviews.findIndex(
        (r) => r.id === data.updateReview.id
      );
      if (index >= 0 && data.updateReview.archived) {
        // Append to archived reviews.
        const [review] = reviews.splice(index, 1);
        archivedReviews.push(review);
      } else if (archiveIndex >= 0 && !data.updateReview.archived) {
        // Remove from archived reviews & append to reviews.
        const [review] = archivedReviews.splice(archiveIndex, 1);
        reviews.push(review);
      }
    } else if (data.createReview) {
      // Add new review into Apollo cache.
      reviews.push(new Review(data.createReview));
    } else if (data.deleteReview !== undefined) {
      // Delete review from Apollo cache.
      const activeIndex = reviews.findIndex(
        (r) => r.id === metadata.review.id
      );
      const archivedIndex = archivedReviews.findIndex(
        (r) => r.id === metadata.review.id
      );
      if (activeIndex !== -1) {
        reviews.splice(activeIndex, 1);
      } else if (archivedIndex !== -1) {
        archivedReviews.splice(archivedIndex, 1);
      }
    } else {
      throw { error: 'Unknown mutation occurred' };
    }

    // Write the updated data to the Apollo cache.
    store.writeQuery({
      query: GetReviews,
      data: { reviews, archivedReviews },
    });
  },
};

const state = {};

const mutations = {};

export default {
  namespaced: true,
  actions,
  state,
  mutations,
};
